import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { projectAPI, taskAPI, invoiceAPI, notificationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { FiFolder, FiCheckSquare, FiClock, FiPlus, FiX, FiTrendingUp, FiFileText, FiAlertCircle } from 'react-icons/fi';
import toast from 'react-hot-toast';

const STATUS_COLORS = {
  planning:   { bg:'#ede9fe', txt:'#5b21b6' },
  active:     { bg:'#d1fae5', txt:'#065f46' },
  'on-hold':  { bg:'#fef3c7', txt:'#92400e' },
  completed:  { bg:'#dbeafe', txt:'#1e40af' },
  cancelled:  { bg:'#fee2e2', txt:'#991b1b' },
};

const TASK_STATUS = {
  todo:         { bg:'#f1f5f9', txt:'#475569', dot:'#94a3b8' },
  'in-progress':{ bg:'#fef3c7', txt:'#92400e', dot:'#f59e0b' },
  'in-review':  { bg:'#e0e7ff', txt:'#3730a3', dot:'#6366f1' },
  completed:    { bg:'#d1fae5', txt:'#065f46', dot:'#22c55e' },
};

export default function ClientPortal() {
  const { user } = useAuth();
  const [projects, setProjects]       = useState([]);
  const [tasks, setTasks]             = useState([]);
  const [invoices, setInvoices]       = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [taskModal, setTaskModal]         = useState(false);
  const [taskForm, setTaskForm]           = useState({ title:'', description:'', priority:'medium', dueDate:'' });
  const [projectReqModal, setProjectReqModal] = useState(false);
  const [projectReqForm, setProjectReqForm]   = useState({ projectName:'', projectType:'', description:'', timeline:'', budgetRange:'' });
  const [sendingReq, setSendingReq]           = useState(false);
  const [loading, setLoading]         = useState(true);

  const load = async () => {
    try {
      const [pR, tR, iR] = await Promise.all([projectAPI.getAll(), taskAPI.getAll(), invoiceAPI.myInvoices().catch(()=>({data:{invoices:[]}}))]);
      setInvoices(iR.data.invoices || []);
      const allProjects = pR.data.projects || [];
      const myProjects = allProjects.filter(p =>
        p.client === user?._id ||
        p.client?._id === user?._id ||
        p.clientId === user?._id ||
        p.clientId?._id === user?._id
      );
      const myProjectIds = new Set(myProjects.map(p => p._id));
      const myTasks = (tR.data.tasks || []).filter(t => {
        const pid = t.project?._id || t.project;
        return myProjectIds.has(pid);
      });
      setProjects(myProjects);
      setTasks(myTasks);
      if (myProjects.length) setActiveProject(myProjects[0]._id);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const submitTask = async () => {
    if (!taskForm.title.trim()) return toast.error('Title required');
    if (!taskForm.dueDate) return toast.error('Due date is required');
    try {
      const payload = { title: taskForm.title, description: taskForm.description, priority: taskForm.priority, project: activeProject, dueDate: taskForm.dueDate };
      await taskAPI.create(payload);
      toast.success('✅ Request submitted! The team has been notified.');
      setTaskModal(false);
      setTaskForm({ title:'', description:'', priority:'medium', dueDate:'' });
      load();
    } catch { toast.error('Failed to submit request'); }
  };

  const submitProjectRequest = async () => {
    if (!projectReqForm.projectName.trim() || !projectReqForm.description.trim())
      return toast.error('Project name and description are required');
    setSendingReq(true);
    try {
      await notificationAPI.sendProjectRequest(projectReqForm);
      toast.success('🚀 Request sent to admin! We\'ll get back to you soon.');
      setProjectReqModal(false);
      setProjectReqForm({ projectName:'', projectType:'', description:'', timeline:'', budgetRange:'' });
    } catch { toast.error('Failed to send request'); }
    setSendingReq(false);
  };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTop:'3px solid var(--pm-primary)', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const activePrj  = projects.find(p => p._id === activeProject);
  const prjTasks   = tasks.filter(t => t.project === activeProject || t.project?._id === activeProject);
  const donePct    = prjTasks.length ? Math.round((prjTasks.filter(t => t.status === 'completed').length / prjTasks.length) * 100) : 0;
  const sc         = STATUS_COLORS[activePrj?.status] || STATUS_COLORS.planning;

  const S = {
    card:  { background:'#fff', borderRadius:16, padding:24, boxShadow:'0 2px 12px #0000000a', border:'1px solid #f1f5f9' },
    btn:   (v) => ({ padding:'9px 18px', borderRadius:10, border:'none', fontWeight:600, fontSize:13, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6,
      ...(v==='primary' ? { background:'var(--pm-gradient)', color:'#fff', boxShadow:'0 4px 14px rgba(var(--pm-primary-rgb),0.2)' }
        :                 { background:'#f8fafc', color:'#64748b', border:'1.5px solid #e2e8f0' }) }),
    inp:   { width:'100%', padding:'9px 13px', border:'1.5px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', boxSizing:'border-box', marginTop:4, fontFamily:"'Inter',sans-serif" },
    overlay:{ position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
    modal: { background:'#fff', borderRadius:20, padding:32, maxWidth:500, width:'90%', boxShadow:'0 24px 64px #00000022' },
  };

  return (
    <div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Page Header ──────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:28, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:28, fontWeight:800, color:'#0f172a' }}>
            👋 Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:14 }}>Here's an overview of your projects and tasks</p>
        </div>
        <button style={{ ...S.btn('primary'), padding:'11px 22px', fontSize:14 }}
          onClick={() => setProjectReqModal(true)}>
          <FiPlus size={16}/> Request New Project
        </button>
      </div>

      {/* ── KPI Cards ────────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:28 }}>
        {[
          { icon:<FiFolder size={20}/>,     label:'Your Projects',  value:projects.length,                                       color:'var(--pm-primary)' },
          { icon:<FiCheckSquare size={20}/>, label:'Total Tasks',    value:tasks.length,                                          color:'#22c55e' },
          { icon:<FiClock size={20}/>,       label:'In Progress',    value:tasks.filter(t=>t.status==='in-progress').length,      color:'#f59e0b' },
          { icon:<FiTrendingUp size={20}/>,  label:'Completed',      value:tasks.filter(t=>t.status==='completed').length,        color:'#10b981' },
        ].map(s => (
          <div key={s.label} style={{ ...S.card, borderLeft:`4px solid ${s.color}`, display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:`${s.color}15`, display:'flex', alignItems:'center', justifyContent:'center', color:s.color, flexShrink:0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize:26, fontWeight:800, color:'#0f172a', lineHeight:1 }}>{s.value}</div>
              <div style={{ fontSize:12, color:'#64748b', marginTop:4 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Invoice Alert Banner ────────────────────────────────── */}
      {invoices.filter(i => !['paid','cancelled'].includes(i.status)).length > 0 && (
        <div style={{ background:'linear-gradient(135deg,#fffbeb,#fef3c7)', border:'1.5px solid #fde68a', borderRadius:14, padding:'14px 20px', marginBottom:20, display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <FiAlertCircle size={20} color="#f59e0b"/>
            <div>
              <span style={{ fontWeight:700, color:'#92400e', fontSize:14 }}>
                {invoices.filter(i => !['paid','cancelled'].includes(i.status)).length} pending invoice(s)
              </span>
              <span style={{ color:'#78350f', fontSize:13, marginLeft:8 }}>
                — Total due: ₹{invoices.filter(i=>!['paid','cancelled'].includes(i.status)).reduce((s,i)=>s+(parseFloat(i.amountInINR||i.total)||0),0).toLocaleString('en-IN',{minimumFractionDigits:2})}
              </span>
            </div>
          </div>
          <Link to="/my-invoices" style={{ padding:'7px 16px', borderRadius:9, background:'#f59e0b', color:'#fff', fontWeight:700, fontSize:13, textDecoration:'none' }}>
            View Invoices →
          </Link>
        </div>
      )}

      {/* ── Main Content ─────────────────────────────────────────── */}
      <div style={{ display:'grid', gridTemplateColumns:'clamp(220px,280px,28%) 1fr', gap:20 }}>

        {/* Project List */}
        <div style={S.card}>
          <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:'#0f172a' }}>Your Projects</h3>
          {projects.length === 0 && (
            <div style={{ textAlign:'center', padding:'32px 0', color:'#94a3b8', fontSize:13 }}>
              <FiFolder size={32} style={{ marginBottom:8, opacity:0.4 }}/><br/>No projects assigned yet
            </div>
          )}
          {projects.map(p => {
            const psc = STATUS_COLORS[p.status] || STATUS_COLORS.planning;
            const isActive = activeProject === p._id;
            return (
              <div key={p._id} onClick={() => setActiveProject(p._id)}
                style={{ padding:'12px 14px', borderRadius:12, cursor:'pointer', marginBottom:6, transition:'all .2s',
                  background: isActive ? 'var(--pm-primary-lighter, rgba(99,102,241,0.06))' : '#f8fafc',
                  border:`1.5px solid ${isActive ? 'var(--pm-primary)' : 'transparent'}` }}>
                <div style={{ fontWeight:600, fontSize:13, color: isActive ? 'var(--pm-primary)' : '#0f172a', marginBottom:4 }}>{p.name}</div>
                <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:6, background:psc.bg, color:psc.txt, textTransform:'capitalize' }}>{p.status}</span>
                  <span style={{ fontSize:11, color:'#94a3b8', textTransform:'capitalize' }}>{p.projectType}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Active Project Detail */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {activePrj ? (
            <>
              {/* Project Header Card */}
              <div style={S.card}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                  <div>
                    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:6 }}>
                      <h2 style={{ margin:0, fontSize:20, fontWeight:800, color:'#0f172a' }}>{activePrj.name}</h2>
                      <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:8, background:sc.bg, color:sc.txt, textTransform:'capitalize' }}>{activePrj.status}</span>
                    </div>
                    <p style={{ margin:0, color:'#64748b', fontSize:13, lineHeight:1.6 }}>{activePrj.description || 'No description provided.'}</p>
                  </div>
                  <button style={S.btn('primary')} onClick={() => setTaskModal(true)}>
                    <FiPlus size={14}/> New Request
                  </button>
                </div>

                {/* Progress Bar */}
                <div style={{ background:'#f8fafc', borderRadius:12, padding:'14px 18px' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                    <span style={{ fontSize:12, fontWeight:700, color:'#475569' }}>Overall Progress</span>
                    <span style={{ fontSize:13, fontWeight:800, color:'var(--pm-primary)' }}>{donePct}%</span>
                  </div>
                  <div style={{ height:8, background:'#e2e8f0', borderRadius:4, overflow:'hidden', marginBottom:10 }}>
                    <div style={{ height:'100%', width:`${donePct}%`, background:'var(--pm-gradient)', borderRadius:4, transition:'width .8s' }}/>
                  </div>
                  <div style={{ display:'flex', gap:20, fontSize:12, color:'#64748b' }}>
                    <span>✅ {prjTasks.filter(t=>t.status==='completed').length} done</span>
                    <span>⏳ {prjTasks.filter(t=>t.status==='in-progress').length} in progress</span>
                    <span>📋 {prjTasks.filter(t=>t.status==='todo').length} todo</span>
                  </div>
                </div>
              </div>

              {/* Tasks Card */}
              <div style={S.card}>
                <h3 style={{ margin:'0 0 16px', fontSize:14, fontWeight:700, color:'#0f172a' }}>
                  Tasks <span style={{ fontWeight:500, color:'#94a3b8' }}>({prjTasks.length})</span>
                </h3>
                {prjTasks.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'36px 0', color:'#94a3b8' }}>
                    <FiCheckSquare size={36} style={{ marginBottom:10, opacity:0.3 }}/><br/>
                    <span style={{ fontSize:13 }}>No tasks yet. Submit a request above!</span>
                  </div>
                ) : (
                  <div>
                    {prjTasks.map(t => {
                      const ts = TASK_STATUS[t.status] || TASK_STATUS.todo;
                      return (
                        <div key={t._id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid #f8fafc' }}>
                          <div style={{ width:8, height:8, borderRadius:'50%', background:ts.dot, flexShrink:0 }}/>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:600, fontSize:13, color: t.status==='completed' ? '#94a3b8' : '#0f172a', textDecoration: t.status==='completed' ? 'line-through' : 'none', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                              {t.title}
                            </div>
                            <div style={{ display:'flex', gap:10, marginTop:3, flexWrap:'wrap' }}>
                              {t.assignedTo?.length > 0 && (
                                <span style={{ fontSize:11, color:'var(--pm-primary)', fontWeight:600 }}>
                                  👤 {t.assignedTo.map(u => u.name || u).join(', ')}
                                </span>
                              )}
                              {t.dueDate && (
                                <span style={{ fontSize:11, color:'#f59e0b', fontWeight:600 }}>
                                  📅 {new Date(t.dueDate).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}
                                </span>
                              )}
                            </div>
                          </div>
                          <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:8, background:ts.bg, color:ts.txt, textTransform:'capitalize', flexShrink:0 }}>
                            {t.status.replace('-',' ')}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div style={{ ...S.card, textAlign:'center', padding:60, color:'#94a3b8' }}>
              <FiFolder size={48} style={{ marginBottom:16, opacity:0.2 }}/><br/>
              <span style={{ fontSize:13 }}>Select a project from the left to view details</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Request New Project Modal ──────────────────────────────── */}
      {projectReqModal && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setProjectReqModal(false)}>
          <div style={S.modal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <div>
                <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:'#0f172a' }}>🚀 Request New Project</h3>
                <p style={{ margin:'4px 0 0', fontSize:12, color:'#94a3b8' }}>Tell us about your new project or work requirement</p>
              </div>
              <button style={{ background:'#f1f5f9', border:'none', cursor:'pointer', width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b' }} onClick={() => setProjectReqModal(false)}>
                <FiX size={16}/>
              </button>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Project / Work Name *</label>
                <input style={S.inp} value={projectReqForm.projectName} onChange={e=>setProjectReqForm({...projectReqForm,projectName:e.target.value})} placeholder="e.g. E-commerce Website, Mobile App, SEO Campaign…"/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Type</label>
                  <select style={S.inp} value={projectReqForm.projectType} onChange={e=>setProjectReqForm({...projectReqForm,projectType:e.target.value})}>
                    <option value="">Select type…</option>
                    <option value="Web Development">Web Development</option>
                    <option value="Mobile App">Mobile App</option>
                    <option value="UI/UX Design">UI/UX Design</option>
                    <option value="Digital Marketing">Digital Marketing</option>
                    <option value="SEO">SEO</option>
                    <option value="Branding">Branding</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Desired Timeline</label>
                  <select style={S.inp} value={projectReqForm.timeline} onChange={e=>setProjectReqForm({...projectReqForm,timeline:e.target.value})}>
                    <option value="">Select timeline…</option>
                    <option value="ASAP">ASAP</option>
                    <option value="Within 1 month">Within 1 month</option>
                    <option value="1–3 months">1–3 months</option>
                    <option value="3–6 months">3–6 months</option>
                    <option value="6+ months">6+ months</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Project Details *</label>
                <textarea style={{ ...S.inp, resize:'vertical', minHeight:100 }} value={projectReqForm.description} onChange={e=>setProjectReqForm({...projectReqForm,description:e.target.value})} placeholder="Describe what you need, goals, target audience, any specific requirements…"/>
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Budget Range (optional)</label>
                <select style={S.inp} value={projectReqForm.budgetRange} onChange={e=>setProjectReqForm({...projectReqForm,budgetRange:e.target.value})}>
                  <option value="">Prefer not to say</option>
                  <option value="Under ₹25,000">Under ₹25,000</option>
                  <option value="₹25,000 – ₹1,00,000">₹25,000 – ₹1,00,000</option>
                  <option value="₹1,00,000 – ₹5,00,000">₹1,00,000 – ₹5,00,000</option>
                  <option value="₹5,00,000+">₹5,00,000+</option>
                  <option value="To be discussed">To be discussed</option>
                </select>
              </div>
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:24 }}>
              <button style={S.btn('ghost')} onClick={() => setProjectReqModal(false)}>Cancel</button>
              <button style={{ ...S.btn('primary'), opacity: sendingReq ? .7 : 1 }} onClick={submitProjectRequest} disabled={sendingReq}>
                <FiPlus size={14}/> {sendingReq ? 'Sending…' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── New Request Modal (task within project) ──────────────────── */}
      {taskModal && (
        <div style={S.overlay} onClick={e => e.target === e.currentTarget && setTaskModal(false)}>
          <div style={S.modal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <div>
                <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:'#0f172a' }}>Submit a Request</h3>
                <p style={{ margin:'4px 0 0', fontSize:12, color:'#94a3b8' }}>For: {activePrj?.name}</p>
              </div>
              <button style={{ background:'#f1f5f9', border:'none', cursor:'pointer', width:32, height:32, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', color:'#64748b' }} onClick={() => setTaskModal(false)}>
                <FiX size={16}/>
              </button>
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>What do you need? *</label>
                <input style={S.inp} value={taskForm.title} onChange={e=>setTaskForm({...taskForm,title:e.target.value})} placeholder="e.g. Fix login bug, Update homepage..."/>
              </div>
              <div>
                <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Details</label>
                <textarea style={{ ...S.inp, resize:'vertical', minHeight:80 }} value={taskForm.description} onChange={e=>setTaskForm({...taskForm,description:e.target.value})} placeholder="Describe what you need..."/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Priority</label>
                  <select style={S.inp} value={taskForm.priority} onChange={e=>setTaskForm({...taskForm,priority:e.target.value})}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', fontSize:12, fontWeight:700, color:'#64748b', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:4 }}>Due Date *</label>
                  <input type="date" required style={{...S.inp, borderColor: !taskForm.dueDate ? '#fca5a5' : undefined}} value={taskForm.dueDate} onChange={e=>setTaskForm({...taskForm,dueDate:e.target.value})}/>
                </div>
              </div>
              {(() => {
                const memberCount = (activePrj?.teamMembers || []).length;
                return (
                  <div style={{ background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:10, padding:'10px 14px', display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ fontSize:16 }}>ℹ️</span>
                    <span style={{ fontSize:12, color:'#0369a1', lineHeight:1.5 }}>
                      {memberCount === 1
                        ? 'Your request will be automatically assigned to the project team member.'
                        : 'Your request will be sent to the admin, who will assign it to the right team member.'}
                    </span>
                  </div>
                );
              })()}
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:24 }}>
              <button style={S.btn('ghost')} onClick={() => setTaskModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={submitTask}><FiPlus size={14}/> Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
